import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, X, Utensils, Dumbbell, Droplets, Scale, ArrowRight, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_ICONS = {
  log_meal: Utensils,
  log_exercise: Dumbbell,
  drink_water: Droplets,
  log_weight: Scale,
  keep_going: Zap,
};

const TYPE_COLORS = {
  reminder: 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/20',
  encouragement: 'border-l-green-500 bg-green-50 dark:bg-green-900/20',
  insight: 'border-l-purple-500 bg-purple-50 dark:bg-purple-900/20',
  celebration: 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
};

const CoachingNudges = ({ token, onAction }) => {
  const { t } = useTranslation();
  const [nudges, setNudges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchNudges();
  }, [token]);

  const fetchNudges = async () => {
    try {
      const res = await axios.get(`${API}/coaching/nudges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNudges(res.data.nudges || []);
    } catch (err) {
      console.error('Error fetching nudges:', err);
    } finally {
      setLoading(false);
    }
  };

  const dismiss = async () => {
    setDismissed(true);
    try {
      await axios.post(`${API}/coaching/nudges/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Error dismissing nudges:', err);
    }
  };

  const handleAction = (action) => {
    const actionMap = {
      log_meal: 'food-capture',
      log_exercise: 'exercise-tracker',
      drink_water: 'water',
      log_weight: 'weight',
      keep_going: null,
    };
    const view = actionMap[action];
    if (view && onAction) onAction(view);
  };

  if (loading || dismissed || nudges.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="coaching-nudges">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-[#FF6600]" />
          <h3 className="font-semibold text-sm text-gray-800 dark:text-white">{t('coaching.title')}</h3>
        </div>
        <button
          data-testid="dismiss-nudges-btn"
          onClick={dismiss}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <X size={14} className="text-gray-400" />
        </button>
      </div>

      {nudges.map((nudge, idx) => {
        const Icon = ACTION_ICONS[nudge.action] || Sparkles;
        const colorClass = TYPE_COLORS[nudge.type] || TYPE_COLORS.encouragement;

        return (
          <div
            key={idx}
            data-testid={`nudge-${idx}`}
            className={`border-l-4 rounded-lg p-3 ${colorClass} cursor-pointer hover:shadow-sm transition-shadow`}
            onClick={() => handleAction(nudge.action)}
          >
            <div className="flex items-start gap-3">
              <Icon size={20} className="mt-0.5 text-gray-600 dark:text-gray-300 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-gray-800 dark:text-white">{nudge.title}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{nudge.message}</p>
              </div>
              {nudge.action && nudge.action !== 'keep_going' && (
                <ArrowRight size={16} className="text-gray-400 flex-shrink-0 mt-1" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CoachingNudges;
